import React from 'react';
import { useNavigate } from 'react-router-dom';
import { SocialAccount, SocialProvider as SocialProviderType } from '../types/social';

// Única fuente de auth del frontend: un JWT firmado por el backend.
// El frontend NUNCA conoce su propio user_id — si lo necesita, lo pide a /drupi/userid.
const JWT_KEY = 'drupi_social_jwt';

const API_BASE = process.env.REACT_APP_API_BASE_URL || '';

const TIKTOK_AUTH_URL    = process.env.REACT_APP_TIKTOK_AUTH_URL    || '';
const INSTAGRAM_AUTH_URL = process.env.REACT_APP_INSTAGRAM_AUTH_URL || '';
const GOOGLE_AUTH_URL    = process.env.REACT_APP_GOOGLE_AUTH_URL    || '';
const FAKE_AUTH_URL      = process.env.REACT_APP_FAKESOCIALMEDIA_AUTH_URL || '';

const AUTH_URLS: Record<SocialProviderType, string> = {
  tiktok:    TIKTOK_AUTH_URL,
  instagram: INSTAGRAM_AUTH_URL,
  google:    GOOGLE_AUTH_URL,
  fake:      FAKE_AUTH_URL,
};

// ── JWT helpers ──────────────────────────────────────────────────────────────

const getJwt   = (): string | null => localStorage.getItem(JWT_KEY);
const setJwt   = (token: string)   => localStorage.setItem(JWT_KEY, token);
const clearJwt = ()                => localStorage.removeItem(JWT_KEY);

const authHeaders = (): HeadersInit => {
  const jwt = getJwt();
  return jwt ? { 'Authorization': `Bearer ${jwt}` } : {};
};

async function deleteAccountFromBackend(provider: SocialProviderType, socialId: string): Promise<void> {
  try {
    await fetch(`${API_BASE}/drupi/accounts/${provider}/${socialId}`, {
      method:  'DELETE',
      headers: authHeaders(),
    });
  } catch (e) {
    console.warn('[Drupi] deleteAccountFromBackend failed (non-blocking):', e);
  }
}

export interface SocialState {
  accounts: SocialAccount[];
  loading: boolean;
  isAuthenticated: boolean;
  authError: string | null;
  clearAuthError: () => void;
  connectProvider: (provider: SocialProviderType) => void;
  disconnectAccount: (accountId: string) => void;
  deleteAllData: () => Promise<void>;
  logout: () => void;
  refreshAccounts: () => Promise<void>;
}

const SocialContext = React.createContext<SocialState>({
  accounts: [],
  loading: false,
  isAuthenticated: false,
  authError: null,
  clearAuthError: () => {},
  connectProvider: () => {},
  disconnectAccount: () => {},
  deleteAllData: async () => {},
  logout: () => {},
  refreshAccounts: async () => {},
});

// Capturar params de la URL SINCRÓNICAMENTE al cargar el módulo, antes de
// que cualquier useEffect de páginas hijas pueda llamar a navigate() y limpiar la URL.
const _initialParams       = new URLSearchParams(window.location.search);
const _initialPendingProv  = sessionStorage.getItem('pending_oauth_provider');

export const SocialProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
  const navigate = useNavigate();
  const [accounts, setAccounts]      = React.useState<SocialAccount[]>([]);
  // Start as `true` when a JWT exists in storage: we *will* fire refreshAccounts()
  // on mount, and consumers (AppLayout) rely on `loading` to avoid flashing the
  // AccessPage before the initial fetch resolves. Without this the orphan-JWT
  // check (`isAuthenticated && !loading && accounts.length === 0`) trips on the
  // very first render because accounts is still [] before the request fires.
  const [loading, setLoading]        = React.useState<boolean>(() => !!getJwt());
  const [isAuthenticated, setAuthed] = React.useState<boolean>(() => !!getJwt());
  const [authError, setAuthError]    = React.useState<string | null>(null);

  // Clear OAuth-callback query params from the URL via react-router so its
  // internal state stays in sync with window.location. Calling
  // window.history.replaceState directly bypasses the router and silently
  // breaks every subsequent useLocation() / navigate() in the session.
  const clearUrlParams = React.useCallback(() => {
    navigate(window.location.pathname, { replace: true });
  }, [navigate]);

  const refreshAccounts = React.useCallback(async () => {
    if (!getJwt()) {
      setAccounts([]);
      setAuthed(false);
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${API_BASE}/drupi/accounts`, { headers: authHeaders() });
      if (res.status === 401) {
        // JWT inválido/expirado
        clearJwt();
        setAuthed(false);
        setAccounts([]);
        return;
      }
      if (!res.ok) return;
      const data: any[] = await res.json();
      const restored: SocialAccount[] = data
        .filter(a => !a.disconnected)
        .map(a => ({
          id:          a._id,
          provider:    a.platform as SocialProviderType,
          socialId:    a.social_id,
          // Nunca mostrar el open_id crudo: si TikTok no dio display_name
          // (grant parcial de scopes) caemos al @handle y, en última instancia,
          // a un genérico — jamás al social_id (que es el open_id de TikTok).
          username:    a.username || a.tiktok_handle || 'Usuario',
          avatarUrl:   a.avatar_url || undefined,
          connectedAt: a.connected_at || new Date().toISOString(),
          followerCount: typeof a.follower_count === 'number' ? a.follower_count : undefined,
          tiktokHandle:   a.tiktok_handle || undefined,
          bioDescription: a.bio_description || undefined,
          isVerified:     a.is_verified === true ? true : undefined,
          scopes:         Array.isArray(a.scopes) ? a.scopes : undefined,
          accessToken: undefined,
        }));
      setAccounts(restored);
      setAuthed(true);
    } catch (e) {
      console.warn('[Drupi] refreshAccounts failed:', e);
    } finally {
      setLoading(false);
    }
  }, []);

  // Al montar: procesar OAuth callback si hay ?jwt=... en la URL, luego refrescar.
  // Usamos los params capturados sincrónicamente al cargar el módulo para evitar
  // la race condition donde ReelPage.useEffect llama navigate() antes que este efecto.
  React.useEffect(() => {
    (async () => {
      const params          = _initialParams;
      const pendingProvider = _initialPendingProv;

      // Errores de OAuth
      const tiktokError = params.get('tiktok_error');
      const igError     = params.get('ig_error');
      const googleError = params.get('google_error');
      if (tiktokError || igError || googleError) {
        sessionStorage.removeItem('pending_oauth_provider');
        const platform = tiktokError ? 'TikTok' : igError ? 'Instagram' : 'Google';
        const msg      = tiktokError || igError || googleError;
        setAuthError(`Error al conectar ${platform}: ${msg}`);
        clearUrlParams();
        return;
      }

      // JWT emitido por el callback
      const newJwt = params.get('jwt');
      if (newJwt) {
        sessionStorage.removeItem('pending_oauth_provider');
        setJwt(newJwt);
        setAuthed(true);
        clearUrlParams();
        await refreshAccounts();
        return;
      }

      // OAuth pendiente sin jwt ni error → fallo silencioso
      if (pendingProvider) {
        // Log all URL params to help diagnose what the backend returned
        const allParams: Record<string, string> = {};
        params.forEach((v, k) => { allParams[k] = v; });
        console.warn(`[Drupi] OAuth silent failure for "${pendingProvider}". URL params:`, allParams);

        sessionStorage.removeItem('pending_oauth_provider');
        const label =
          pendingProvider === 'tiktok'    ? 'TikTok' :
          pendingProvider === 'instagram' ? 'Instagram' :
          pendingProvider === 'google'    ? 'Google' :
          pendingProvider;

        const extraHint = pendingProvider === 'tiktok'
          ? ' Si la cuenta TikTok no fue aprobada en el sandbox de la app, el acceso está restringido.'
          : '';

        setAuthError(
          `No se pudo conectar ${label}. Cancelaste el proceso o el backend no emitió un token.${extraHint}`
        );
      }

      // JWT ya guardado → refrescar cuentas
      if (getJwt()) await refreshAccounts();
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const connectProvider = (provider: SocialProviderType) => {
    const url = AUTH_URLS[provider];
    if (!url) {
      console.error(`[Drupi] OAuth URL for ${provider} is not configured`);
      return;
    }
    sessionStorage.setItem('pending_oauth_provider', provider);
    // Volver a la URL actual. Esto mantiene al usuario en donde estaba:
    // - Si conectó desde AccessPage (pathname='/') → va al Home con videos
    // - Si conectó desde "Mis redes" → vuelve a /connections
    const returnTo = encodeURIComponent(window.location.origin + window.location.pathname);
    const jwt      = getJwt();
    const jwtParam = jwt ? `&jwt=${encodeURIComponent(jwt)}` : '';
    window.location.href = `${url}?return_to=${returnTo}${jwtParam}`;
  };

  const clearAuthError = React.useCallback(() => setAuthError(null), []);

  const disconnectAccount = (accountId: string) => {
    const account = accounts.find(a => a.id === accountId);
    if (account?.socialId) {
      deleteAccountFromBackend(account.provider, account.socialId);
    }
    setAccounts(prev => prev.filter(a => a.id !== accountId));
  };

  // Borra todos los datos del usuario en el backend (Meta Platform Terms 3.d.i).
  const deleteAllData = async () => {
    try {
      await fetch(`${API_BASE}/drupi/user`, {
        method:  'DELETE',
        headers: authHeaders(),
      });
    } catch {
      // Igual limpiamos local
    }
    clearJwt();
    setAccounts([]);
    setAuthed(false);
  };

  // Cierra sesión local sin borrar datos del backend.
  // Al reconectarse via OAuth, el backend devuelve un JWT con el user_id canónico.
  const logout = () => {
    clearJwt();
    setAccounts([]);
    setAuthed(false);
  };

  return (
    <SocialContext.Provider value={{
      accounts, loading, isAuthenticated, authError,
      clearAuthError, connectProvider, disconnectAccount, deleteAllData, logout, refreshAccounts,
    }}>
      {children}
    </SocialContext.Provider>
  );
};

export const useSocialContext = () => React.useContext(SocialContext);
